import { useState, useEffect } from 'react'
import * as React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Loader2, CreditCard, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { apiClient } from '@/services/api'
import { formatCurrency } from '@/lib/utils'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import toast from 'react-hot-toast'

interface WithdrawModalProps {
  isOpen: boolean
  onClose: () => void
  wallets?: any[]
  onSuccess?: () => void
}

export function WithdrawModal({ isOpen, onClose, wallets, onSuccess }: WithdrawModalProps) {
  const { t } = useTranslation()
  const [amount, setAmount] = useState<string>('')
  const [selectedWalletId, setSelectedWalletId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingInfo, setIsLoadingInfo] = useState(false)
  const [lastDepositInfo, setLastDepositInfo] = useState<any>(null)
  const [withdrawInfo, setWithdrawInfo] = useState<any>(null)

  // Set default wallet if wallets available
  React.useEffect(() => {
    if (wallets && wallets.length > 0 && !selectedWalletId) {
      setSelectedWalletId(wallets[0].id)
    }
  }, [wallets, selectedWalletId])

  // Fetch last deposit info when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchLastDepositInfo()
    }
  }, [isOpen])

  // Fetch withdraw calculation when amount changes
  useEffect(() => {
    const calculateWithdraw = async () => {
      if (!amount || parseFloat(amount) <= 0 || !selectedWalletId) {
        setWithdrawInfo(null)
        return
      }

      const amountValue = parseFloat(amount)
      if (isNaN(amountValue)) {
        setWithdrawInfo(null)
        return
      }

      try {
        const response = await apiClient.createWithdrawRequest({
          std_amount: amountValue.toString(),
          wallet_id: selectedWalletId,
        })

        const data = response.data?.data || response.data
        setWithdrawInfo(data)
      } catch (error: any) {
        console.error('Error calculating withdraw:', error)
        setWithdrawInfo(null)
        if (error.response?.data?.error) {
          // Only show error if user has entered amount
          if (amount && parseFloat(amount) > 0) {
            toast.error(error.response.data.error)
          }
        }
      }
    }

    const timeoutId = setTimeout(calculateWithdraw, 1000) // Debounce
    return () => clearTimeout(timeoutId)
  }, [amount, selectedWalletId])

  const fetchLastDepositInfo = async () => {
    setIsLoadingInfo(true)
    try {
      const response = await apiClient.getLastDeposit()
      const data = response.data?.data || response.data
      setLastDepositInfo(data)
    } catch (error: any) {
      console.error('Error fetching last deposit:', error)
      setLastDepositInfo(null)
    } finally {
      setIsLoadingInfo(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedWalletId || !amount || parseFloat(amount) <= 0) {
      toast.error(t('wallet.pleaseEnterAmount') || 'Por favor, insira um valor')
      return
    }

    if (!withdrawInfo) {
      toast.error(t('wallet.calculatingWithdraw') || 'Calculando levantamento...')
      return
    }

    setIsLoading(true)

    try {
      const response = await apiClient.processWithdraw({
        std_amount: parseFloat(amount).toString(),
        wallet_id: selectedWalletId,
        payment_intent_id: withdrawInfo.payment_intent_id,
        refund_amount_cents: withdrawInfo.refund_amount_cents,
        stripe_currency: withdrawInfo.stripe_currency,
      })

      const data = response.data?.data || response.data
      toast.success(t('wallet.withdrawSuccess') || 'Levantamento processado com sucesso!')
      onSuccess?.()
      onClose()
      // Reset form
      setAmount('')
      setWithdrawInfo(null)
    } catch (error: any) {
      console.error('Error processing withdraw:', error)
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail ||
                          t('wallet.withdrawError') || 
                          'Erro ao processar levantamento'
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const selectedWallet = wallets?.find((w: any) => w.id === selectedWalletId)
  const maxAmount = selectedWallet ? parseFloat(selectedWallet.available_balance) : 0

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'EUR': return '€'
      case 'USD': return '$'
      case 'STN':
      case 'STN': return 'Db'
      default: return currency
    }
  }

  const getCardBrandName = (brand: string) => {
    const brands: { [key: string]: string } = {
      'visa': 'Visa',
      'mastercard': 'Mastercard',
      'amex': 'American Express',
      'discover': 'Discover',
    }
    return brands[brand?.toLowerCase()] || brand || 'Cartão'
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[100]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-20 md:pb-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[calc(90vh-5rem)] md:max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-text-primary">
                  {t('wallet.withdrawCard')}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  disabled={isLoading}
                >
                  <X className="h-5 w-5 text-text-secondary" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Info Message */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-blue-800 font-medium mb-1">
                        {t('wallet.withdrawInfo') || 'Informação importante'}
                      </p>
                      <p className="text-xs text-blue-700">
                        {t('wallet.withdrawProcessingTime') || 'O levantamento pode demorar até 42 horas para ser processado. O dinheiro será enviado para o cartão utilizado no último depósito.'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Last Deposit Info */}
                {isLoadingInfo ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                ) : lastDepositInfo?.has_last_deposit && lastDepositInfo?.payment_method?.last4 ? (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-2">
                      <CreditCard className="h-5 w-5 text-text-secondary" />
                      <span className="text-sm font-medium text-text-primary">
                        {t('wallet.lastDepositCard') || 'Cartão do último depósito'}
                      </span>
                    </div>
                    <p className="text-sm text-text-secondary">
                      {getCardBrandName(lastDepositInfo.payment_method.brand)} •••• {lastDepositInfo.payment_method.last4}
                    </p>
                    <p className="text-xs text-text-tertiary mt-1">
                      {t('wallet.lastDepositDate') || 'Último depósito'}: {lastDepositInfo.date ? new Date(lastDepositInfo.date).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      {t('wallet.noPreviousDeposit') || 'Não foi encontrado um depósito anterior. Por favor, faça um depósito antes de levantar dinheiro.'}
                    </p>
                  </div>
                )}

                {/* Wallet Selection */}
                {wallets && wallets.length > 0 && (
                  <div>
                    <label htmlFor="wallet" className="block text-sm font-medium text-text-secondary mb-1">
                      {t('wallet.selectWallet') || 'Selecionar Carteira'}
                    </label>
                    <select
                      id="wallet"
                      value={selectedWalletId}
                      onChange={(e) => setSelectedWalletId(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:outline-none transition-colors"
                      required
                      disabled={isLoading}
                    >
                      {wallets.map((wallet: any) => (
                        <option key={wallet.id} value={wallet.id}>
                          {wallet.currency_display || wallet.currency} - {t('wallet.balance')}: {wallet.available_balance || '0.00'}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Amount Input */}
                <div>
                  <label htmlFor="amount" className="block text-sm font-medium text-text-secondary mb-1">
                    {t('wallet.amountToWithdraw') || 'Valor a levantar'} (STN)
                  </label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value
                      if (value === '' || parseFloat(value) >= 0) {
                        setAmount(value)
                      }
                    }}
                    min="0.01"
                    max={maxAmount.toString()}
                    step="0.01"
                    required
                    className="text-lg"
                  />
                  {selectedWallet && (
                    <p className="text-xs text-text-tertiary mt-1">
                      {t('wallet.availableBalance') || 'Saldo disponível'}: {formatCurrency(maxAmount, 'STN')}
                    </p>
                  )}
                </div>

                {/* Withdraw Calculation */}
                {withdrawInfo && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-secondary">
                        {t('wallet.youWillReceive') || 'Receberá'}
                      </span>
                      <span className="text-lg font-bold text-text-primary">
                        {formatCurrency(parseFloat(withdrawInfo.refund_amount), withdrawInfo.original_currency)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-text-tertiary">
                      <span>{t('wallet.inCurrency') || 'Na moeda'}: {withdrawInfo.original_currency}</span>
                      <span>{t('wallet.processingTime') || 'Tempo de processamento'}: {withdrawInfo.estimated_processing_time}</span>
                    </div>
                  </div>
                )}

                {/* Buttons */}
                <div className="flex space-x-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="flex-1"
                    disabled={isLoading}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={isLoading || !withdrawInfo || !amount || parseFloat(amount) <= 0 || parseFloat(amount) > maxAmount || !lastDepositInfo?.has_last_deposit}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {t('wallet.processing')}
                      </>
                    ) : (
                      t('wallet.withdrawCard')
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

